import React, { useState, useEffect, useCallback, useRef } from "react";
import Select from "react-select";
import axios from "axios";
import countries from "../../utils/countryList";
import phoneCountryCodes from "../../utils/phoneCountryCodes";
import { FiX, FiUser, FiBriefcase, FiMail, FiPhone, FiMapPin, FiSettings, FiFileText, FiDollarSign, FiFolder, FiSave, FiCalendar } from "react-icons/fi";
import UploadDocumentModal from "../UploadDocumentModal";
import { CloudUpload, FileText, Trash2 } from "lucide-react";
import { toast } from "react-toastify";

// Format country list for react-select
const countryOptions = countries.map((country) => ({
    label: `${country.flag} ${country.name}`,
    value: country.name,
}));

// Format phone code list for react-select with searchable country names
const phoneOptions = phoneCountryCodes.map((c) => ({
    label: `${c.flag} ${c.name} (${c.code})`,
    value: c.code,
}));

const FileItem = ({ file, onRemove }) => {
  const [isRemoving, setIsRemoving] = useState(false);

  const handleRemove = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsRemoving(true);
    await onRemove(file);
  };

  const getFileTypeColor = (fileName) => {
    if (!fileName || typeof fileName !== "string") {
      return "text-gray-600 bg-gray-50";
    }
    const ext = fileName.split(".").pop()?.toLowerCase();
    switch (ext) {
      case "pdf":
        return "text-red-600 bg-red-50";
      case "docx":
      case "doc":
        return "text-blue-600 bg-blue-50";
      case "xlsx":
      case "xls":
        return "text-green-600 bg-green-50";
      case "txt":
        return "text-gray-600 bg-gray-50";
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return "text-purple-600 bg-purple-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  return (
    <div className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm transition-all duration-300">
      <div className="flex items-center p-4 space-x-4">
        <div className={`flex-shrink-0 p-3 rounded-lg transition-colors ${getFileTypeColor(file.originalName)}`}>
          <FileText className="w-6 h-6" />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate pr-2">
            {file.originalName}
          </p>
          <div className="flex items-center space-x-3 mt-1">
            <span className="text-xs font-medium text-green-600">Ready</span>
          </div>
        </div>

        <button
          type="button"
          onClick={handleRemove}
          disabled={isRemoving}
          className={`p-2 rounded-lg transition-all duration-200 ${
            isRemoving
              ? "text-gray-300 cursor-not-allowed"
              : "text-gray-400 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100"
          }`}
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

/**
 * A modal form for editing an existing donor's information.
 * It fetches the existing data and allows for modification and saving.
 * @param {string} donorId - The ID of the donor to edit.
 * @param {Function} closeModal - Function to close the modal.
 * @param {Function} onUpdateSuccess - Callback function to execute after a successful update.
 */
export default function EditDonorModal({ donorId, closeModal, onUpdateSuccess }) {
    const modalRef = useRef(null);
    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [uploadedDocuments, setUploadedDocuments] = useState([]);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    
    const token = localStorage.getItem("token");
    const decoded = token ? JSON.parse(atob(token.split('.')[1])) : null;
    const staffId = decoded ? decoded.id : null;

    // Handle click outside to close
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            // Don't close if upload modal is open
            if (isUploadModalOpen) return;
            
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                closeModal();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [closeModal, isUploadModalOpen]);

    /**
     * Fetches the specific donor's data to pre-fill the form.
     */
    const fetchDonorDetails = useCallback(async () => {
        setFetching(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                toast.error("Authentication token not found. Please log in.");
                throw new Error("Authentication token not found. Please log in.");
            }

            const API_URL = "https://ims.holdthechild.net";
            const response = await axios.get(`${API_URL}/api/donors/${donorId}`, {
                headers: { "Authorization": `Bearer ${token}` },
            });

            // Split the phone numbers from the fetched data
            const donorData = response.data.data;
            const phone = {
                primary: { code: "", number: "" },
                secondary: { code: "", number: "" }
            };

            // Helper to split the phone number string
            const splitPhoneNumber = (fullNumber) => {
                if (!fullNumber) return { code: "", number: "" };
                // Find the longest matching code at the start of the string
                const foundCode = phoneCountryCodes.find(c => fullNumber.startsWith(c.code));
                if (foundCode) {
                    return {
                        code: foundCode.code,
                        number: fullNumber.substring(foundCode.code.length)
                    };
                }
                // If no match, assume it's just the number
                return { code: "", number: fullNumber };
            };

            phone.primary = splitPhoneNumber(donorData.phone.primary);
            phone.secondary = splitPhoneNumber(donorData.phone.secondary);

            // Set uploaded documents if they exist
            if (donorData.documents && donorData.documents.length > 0) {
                setUploadedDocuments(donorData.documents);
            }

            // Update the form state with the split phone numbers
            setFormData({ ...donorData, phone });

        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || "Failed to fetch donor details.";
            toast.error(errorMessage);
        } finally {
            setFetching(false);
        }
    }, [donorId]);

    useEffect(() => {
        fetchDonorDetails();
    }, [fetchDonorDetails]);

    /**
     * Handles changes for all form inputs, including nested state objects.
     */
    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({ ...prev, [parent]: { ...prev[parent], [child]: value } }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    /**
     * Handles changes specifically for the phone number inputs (both code and number).
     */
    const handlePhoneChange = (parent, type, value) => {
        setFormData(prev => ({
            ...prev,
            phone: {
                ...prev.phone,
                [parent]: {
                    ...prev.phone[parent],
                    [type]: value
                }
            }
        }));
    };

    /**
     * Handles changes specifically for the react-select component for countries.
     */
    const handleCountryChange = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            address: { ...prev.address, country: selectedOption ? selectedOption.value : "" }
        }));
    };

    const handleUploadSuccess = useCallback((files) => {
        setUploadedDocuments(files);
        setIsUploadModalOpen(false);
        toast.success(`${files.length} document(s) uploaded successfully!`);
    }, []);

    const handleRemoveFile = useCallback(async (file) => {
        setUploadedDocuments(prev => prev.filter(f => f !== file));
    }, []);

    const handleUploadButtonClick = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsUploadModalOpen(true);
    }, []);

    /**
     * Handles the form submission to update the donor's profile.
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const token = localStorage.getItem("token");
        const API_URL = "https://ims.holdthechild.net";

        try {
            if (!token) {
                toast.error("Authentication token not found. Please log in.");
                throw new Error("Authentication token not found. Please log in.");
            }

            // Create a copy of the form data to combine phone numbers before sending
            const submissionData = JSON.parse(JSON.stringify(formData));

            if (submissionData.phone.primary.number) {
                submissionData.phone.primary = submissionData.phone.primary.code + submissionData.phone.primary.number;
            } else {
                submissionData.phone.primary = undefined;
            }

            if (submissionData.phone.secondary.number) {
                submissionData.phone.secondary = submissionData.phone.secondary.code + submissionData.phone.secondary.number;
            } else {
                submissionData.phone.secondary = undefined;
            }

            submissionData.documentIds = uploadedDocuments.map(doc => doc._id || doc.id).filter(Boolean);

            const response = await axios.put(`${API_URL}/api/donors/${donorId}`, submissionData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            toast.success("Donor information updated successfully! 🎉");
            setTimeout(onUpdateSuccess, 1000);

        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || "An unexpected error occurred.";
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Render loading state while fetching donor data
    if (fetching) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50">
                <div className="text-white text-xl flex items-center">
                    <svg className="animate-spin h-5 w-5 mr-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading Donor Data...
                </div>
            </div>
        );
    }

    // Render the form once data is fetched
    return (
        <>
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 backdrop-blur-sm p-4">
                <div 
                    ref={modalRef}
                    className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl relative transform transition-all duration-300 ease-out max-h-[90vh] flex flex-col"
                >
                    {/* Fixed Header with Orange Theme */}
                    <div className="bg-gradient-to-r from-orange-300 to-orange-400 rounded-t-2xl p-4 sm:p-6 border-b border-orange-500 flex-shrink-0">
                        <div className="flex justify-between items-center">
                            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                                    <FiDollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
                                </div>
                                <div>
                                    <h2 className="text-lg sm:text-xl font-bold text-black">
                                        Edit Donor Profile
                                    </h2>
                                    <div className="flex items-center mt-1 text-black text-sm">
                                        <FiFolder className="w-3 h-3 mr-1" />
                                        <span>Programs Department</span>
                                    </div>
                                </div>
                            </div>
                            <button 
                                onClick={closeModal} 
                                className="p-2 rounded-lg transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white"
                            >
                                <FiX size={20} />
                            </button>
                        </div>
                    </div>

                    {/* Scrollable Form Body */}
                    <div className="flex-grow overflow-y-auto p-4 sm:p-6">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {formData && (
                                <>
                                    {/* Donor Identity Section */}
                                    <div className="border border-gray-200 rounded-lg p-4 sm:p-5 shadow-sm">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                            <FiUser className="w-5 h-5 mr-2 text-orange-500" />
                                            Donor Identity
                                        </h3>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                                            {/* Donor Type */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Donor Type <span className="text-red-500">*</span>
                                                </label>
                                                <div className="relative">
                                                    <select
                                                        name="donorType"
                                                        value={formData.donorType}
                                                        onChange={handleChange}
                                                        className="w-full pl-10 pr-10 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 appearance-none"
                                                    >
                                                        <option value="individual">Individual</option>
                                                        <option value="organization">Organization</option>
                                                        <option value="foundation">Foundation</option>
                                                        <option value="government">Government</option>
                                                        <option value="corporate">Corporate</option>
                                                    </select>
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <FiUser className="h-5 w-5 text-gray-400" />
                                                    </div>
                                                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                                        <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                                        </svg>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Donor Category */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Donor Category <span className="text-red-500">*</span>
                                                </label>
                                                <div className="relative">
                                                    <select
                                                        name="donorCategory"
                                                        value={formData.donorCategory}
                                                        onChange={handleChange}
                                                        className="w-full pl-10 pr-10 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 appearance-none"
                                                    >
                                                        <option value="first-time">First-Time</option>
                                                        <option value="recurring">Recurring</option>
                                                        <option value="regular">Regular</option>
                                                        <option value="major">Major</option>
                                                        <option value="planned">Planned</option>
                                                    </select>
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <FiDollarSign className="h-5 w-5 text-gray-400" />
                                                    </div>
                                                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                                        <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                                        </svg>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Full Name */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Full Name {formData.donorType === 'individual' && <span className="text-red-500">*</span>}
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <FiUser className="h-5 w-5 text-gray-400" />
                                                    </div>
                                                    <input
                                                        type="text"
                                                        name="name"
                                                        value={formData.name || ''}
                                                        onChange={handleChange}
                                                        placeholder="Required for Individuals"
                                                        required={formData.donorType === 'individual'}
                                                        className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                                    />
                                                </div>
                                            </div>

                                            {/* Organization Name */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Organization Name {formData.donorType !== 'individual' && <span className="text-red-500">*</span>}
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <FiBriefcase className="h-5 w-5 text-gray-400" />
                                                    </div>
                                                    <input
                                                        type="text"
                                                        name="organization"
                                                        value={formData.organization || ''}
                                                        onChange={handleChange}
                                                        placeholder="Required for Organizations"
                                                        required={formData.donorType !== 'individual'}
                                                        className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                                    />
                                                </div>
                                            </div>

                                            {/* Contact Person */}
                                            <div className="sm:col-span-2">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Contact Person
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <FiUser className="h-5 w-5 text-gray-400" />
                                                    </div>
                                                    <input
                                                        type="text"
                                                        name="contactPerson"
                                                        value={formData.contactPerson || ''}
                                                        onChange={handleChange}
                                                        placeholder="e.g., Jane Doe"
                                                        className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Contact Information Section */}
                                    <div className="border border-gray-200 rounded-lg p-4 sm:p-5 shadow-sm">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                            <FiMail className="w-5 h-5 mr-2 text-orange-500" />
                                            Contact Information
                                        </h3>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                                            {/* Primary Email */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Primary Email
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <FiMail className="h-5 w-5 text-gray-400" />
                                                    </div>
                                                    <input
                                                        type="email"
                                                        name="email"
                                                        value={formData.email || ''}
                                                        onChange={handleChange}
                                                        placeholder="donor@example.com"
                                                        className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                                    />
                                                </div>
                                            </div>

                                            {/* Alternate Email */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Alternate Email
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <FiMail className="h-5 w-5 text-gray-400" />
                                                    </div>
                                                    <input
                                                        type="email"
                                                        name="alternateEmail"
                                                        value={formData.alternateEmail || ''}
                                                        onChange={handleChange}
                                                        placeholder="alternate@example.com"
                                                        className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                                    />
                                                </div>
                                            </div>

                                            {/* Primary Phone */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Primary Phone
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <FiPhone className="h-5 w-5 text-gray-400" />
                                                    </div>
                                                    <div className="flex">
                                                        <Select
                                                            options={phoneOptions}
                                                            value={phoneOptions.find(opt => opt.value === formData.phone?.primary?.code) || null}
                                                            onChange={(selectedOption) => handlePhoneChange('primary', 'code', selectedOption ? selectedOption.value : '')}
                                                            placeholder="Code"
                                                            className="w-1/3 text-sm"
                                                            styles={{
                                                                control: (base, state) => ({
                                                                    ...base,
                                                                    borderRadius: '0.5rem 0 0 0.5rem',
                                                                    minHeight: '44px',
                                                                    borderColor: state.isFocused ? '#F68B1F' : '#D1D5DB',
                                                                    boxShadow: state.isFocused ? '0 0 0 1px #F68B1F' : 'none',
                                                                    '&:hover': {
                                                                        borderColor: '#F68B1F'
                                                                    }
                                                                }),
                                                                option: (base, state) => ({
                                                                    ...base,
                                                                    backgroundColor: state.isSelected ? '#F68B1F' : state.isFocused ? '#FFF5E6' : 'white',
                                                                    color: state.isSelected ? 'white' : '#1F2937',
                                                                    '&:hover': {
                                                                        backgroundColor: '#FFF5E6'
                                                                    }
                                                                }),
                                                            }}
                                                        />
                                                        <input
                                                            type="text"
                                                            value={formData.phone?.primary?.number || ''}
                                                            onChange={(e) => handlePhoneChange('primary', 'number', e.target.value)}
                                                            placeholder="e.g., 123456789"
                                                            className="w-2/3 border border-gray-300 rounded-r-md px-3 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Secondary Phone */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Secondary Phone
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <FiPhone className="h-5 w-5 text-gray-400" />
                                                    </div>
                                                    <div className="flex">
                                                        <Select
                                                            options={phoneOptions}
                                                            value={phoneOptions.find(opt => opt.value === formData.phone?.secondary?.code) || null}
                                                            onChange={(selectedOption) => handlePhoneChange('secondary', 'code', selectedOption ? selectedOption.value : '')}
                                                            placeholder="Code"
                                                            className="w-1/3 text-sm"
                                                            styles={{
                                                                control: (base, state) => ({
                                                                    ...base,
                                                                    borderRadius: '0.5rem 0 0 0.5rem',
                                                                    minHeight: '44px',
                                                                    borderColor: state.isFocused ? '#F68B1F' : '#D1D5DB',
                                                                    boxShadow: state.isFocused ? '0 0 0 1px #F68B1F' : 'none',
                                                                    '&:hover': {
                                                                        borderColor: '#F68B1F'
                                                                    }
                                                                }),
                                                                option: (base, state) => ({
                                                                    ...base,
                                                                    backgroundColor: state.isSelected ? '#F68B1F' : state.isFocused ? '#FFF5E6' : 'white',
                                                                    color: state.isSelected ? 'white' : '#1F2937',
                                                                    '&:hover': {
                                                                        backgroundColor: '#FFF5E6'
                                                                    }
                                                                }),
                                                            }}
                                                        />
                                                        <input
                                                            type="text"
                                                            value={formData.phone?.secondary?.number || ''}
                                                            onChange={(e) => handlePhoneChange('secondary', 'number', e.target.value)}
                                                            placeholder="e.g., 987654321"
                                                            className="w-2/3 border border-gray-300 rounded-r-md px-3 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Address Details Section */}
                                    <div className="border border-gray-200 rounded-lg p-4 sm:p-5 shadow-sm">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                            <FiMapPin className="w-5 h-5 mr-2 text-orange-500" />
                                            Address Details
                                        </h3>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                                            {/* Street Address */}
                                            <div className="sm:col-span-2">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Street Address
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <FiMapPin className="h-5 w-5 text-gray-400" />
                                                    </div>
                                                    <input
                                                        type="text"
                                                        name="address.street"
                                                        value={formData.address?.street || ''}
                                                        onChange={handleChange}
                                                        placeholder="e.g., Plot 123, Main Street"
                                                        className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                                    />
                                                </div>
                                            </div>

                                            {/* City / Town */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    City / Town
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <FiMapPin className="h-5 w-5 text-gray-400" />
                                                    </div>
                                                    <input
                                                        type="text"
                                                        name="address.city"
                                                        value={formData.address?.city || ''}
                                                        onChange={handleChange}
                                                        placeholder="e.g., Kampala"
                                                        className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                                    />
                                                </div>
                                            </div>

                                            {/* State / Province */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    State / Province
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <FiMapPin className="h-5 w-5 text-gray-400" />
                                                    </div>
                                                    <input
                                                        type="text"
                                                        name="address.state"
                                                        value={formData.address?.state || ''}
                                                        onChange={handleChange}
                                                        placeholder="e.g., Central Region"
                                                        className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                                    />
                                                </div>
                                            </div>

                                            {/* Postal Code */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Postal Code
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <FiMapPin className="h-5 w-5 text-gray-400" />
                                                    </div>
                                                    <input
                                                        type="text"
                                                        name="address.postalCode"
                                                        value={formData.address?.postalCode || ''}
                                                        onChange={handleChange}
                                                        placeholder="e.g., 12345"
                                                        className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                                                    />
                                                </div>
                                            </div>

                                            {/* Country */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Country
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <FiMapPin className="h-5 w-5 text-gray-400" />
                                                    </div>
                                                    <Select
                                                        options={countryOptions}
                                                        value={countryOptions.find(opt => opt.value === formData.address?.country) || null}
                                                        onChange={handleCountryChange}
                                                        placeholder="Select country..."
                                                        className="w-full pl-10 pr-10 py-0"
                                                        styles={{
                                                            control: (base, state) => ({
                                                                ...base,
                                                                borderRadius: '0.5rem',
                                                                minHeight: '44px',
                                                                borderColor: state.isFocused ? '#F68B1F' : '#D1D5DB',
                                                                boxShadow: state.isFocused ? '0 0 0 1px #F68B1F' : 'none',
                                                                '&:hover': {
                                                                    borderColor: '#F68B1F'
                                                                }
                                                            }),
                                                            option: (base, state) => ({
                                                                ...base,
                                                                backgroundColor: state.isSelected ? '#F68B1F' : state.isFocused ? '#FFF5E6' : 'white',
                                                                color: state.isSelected ? 'white' : '#1F2937',
                                                                '&:hover': {
                                                                    backgroundColor: '#FFF5E6'
                                                                }
                                                            }),
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Administrative Details Section */}
                                    <div className="border border-gray-200 rounded-lg p-4 sm:p-5 shadow-sm">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                            <FiSettings className="w-5 h-5 mr-2 text-orange-500" />
                                            Administrative Details
                                        </h3>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                                            {/* Status */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Status
                                                </label>
                                                <div className="relative">
                                                    <select
                                                        name="status"
                                                        value={formData.status || ''}
                                                        onChange={handleChange}
                                                        className="w-full pl-10 pr-10 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 appearance-none"
                                                    >
                                                        <option value="active">Active</option>
                                                        <option value="inactive">Inactive</option>
                                                        <option value="do_not_contact">Do Not Contact</option>
                                                    </select>
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <FiSettings className="h-5 w-5 text-gray-400" />
                                                    </div>
                                                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                                        <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                                        </svg>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Communication Preference */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Communication Preference
                                                </label>
                                                <div className="relative">
                                                    <select
                                                        name="preferences.communication"
                                                        value={formData.preferences?.communication || ''}
                                                        onChange={handleChange}
                                                        className="w-full pl-10 pr-10 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 appearance-none"
                                                    >
                                                        <option value="email">Email</option>
                                                        <option value="phone">Phone</option>
                                                        <option value="sms">SMS</option>
                                                        <option value="none">None</option>
                                                    </select>
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <FiMail className="h-5 w-5 text-gray-400" />
                                                    </div>
                                                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                                        <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                                        </svg>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Communication Frequency */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Communication Frequency
                                                </label>
                                                <div className="relative">
                                                    <select
                                                        name="preferences.frequency"
                                                        value={formData.preferences?.frequency || ''}
                                                        onChange={handleChange}
                                                        className="w-full pl-10 pr-10 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 appearance-none"
                                                    >
                                                        <option value="unspecified">Unspecified</option>
                                                        <option value="monthly">Monthly</option>
                                                        <option value="quarterly">Quarterly</option>
                                                        <option value="yearly">Yearly</option>
                                                    </select>
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <FiCalendar className="h-5 w-5 text-gray-400" />
                                                    </div>
                                                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                                        <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                                        </svg>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* General Notes */}
                                            <div className="sm:col-span-2">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    General Notes
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute top-3 left-3 flex items-start pointer-events-none">
                                                        <FiFileText className="h-5 w-5 text-gray-400" />
                                                    </div>
                                                    <textarea
                                                        name="notes"
                                                        value={formData.notes || ''}
                                                        onChange={handleChange}
                                                        placeholder="Any other relevant information..."
                                                        rows={3}
                                                        className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 resize-none"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Supporting Documents Section */}
                                    <div className="border border-gray-200 rounded-lg p-4 sm:p-5 shadow-sm">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                            <CloudUpload className="w-5 h-5 mr-2 text-orange-500" />
                                            Supporting Documents
                                            {uploadedDocuments.length > 0 && (
                                                <span className="ml-2 inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                                                    {uploadedDocuments.length} file(s) ready
                                                </span>
                                            )}
                                        </h3>
                                        
                                        <button
                                            type="button"
                                            onClick={handleUploadButtonClick}
                                            disabled={loading}
                                            className="w-full border-2 border-dashed border-gray-300 rounded-md p-6 text-center text-gray-500 hover:border-orange-500 hover:text-orange-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <CloudUpload className="mx-auto h-8 w-8 mb-2" />
                                            Click to Upload Documents
                                        </button>

                                        {uploadedDocuments.length > 0 && (
                                            <div className="mt-4 space-y-2">
                                                <h4 className="text-sm font-medium text-gray-800">
                                                    Uploaded Files ({uploadedDocuments.length})
                                                </h4>
                                                <div className="space-y-2">
                                                    {uploadedDocuments.map((file, index) => (
                                                        <FileItem
                                                            key={`${file.originalName}-${index}`}
                                                            file={file}
                                                            onRemove={handleRemoveFile}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex flex-col sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
                                        <button
                                            type="button"
                                            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-all duration-200 order-2 sm:order-1"
                                            onClick={closeModal}
                                            disabled={loading}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-orange-400 to-orange-500 rounded-lg hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200 flex items-center justify-center space-x-2 order-1 sm:order-2"
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    <span>Saving...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <FiSave className="w-4 h-4" />
                                                    <span>Save Changes</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </>
                            )}
                        </form>
                    </div>
                </div>
            </div>

            <UploadDocumentModal
                isOpen={isUploadModalOpen}
                onClose={() => setIsUploadModalOpen(false)}
                staffId={staffId}
                onUploadSuccessWithFiles={handleUploadSuccess}
                category="Donor Documents"
            />
        </>
    );
}